// Card Product Event Handlers
// This file handles product card behaviors including video playback and swatch expand/collapse

// Extracted function to initialize product card handlers - can be called multiple times
function initializeProductCardHandlers(containerElement = document) {
  // Handle video autoplay in product cards
  const productCards = containerElement.querySelectorAll(".card-wrapper");

  productCards.forEach((card) => {
    // Skip if already initialized to avoid duplicate listeners
    if (card.dataset.handlersInitialized === "true") {
      return;
    }

    card.dataset.handlersInitialized = "true";

    const mediaSlides = card.querySelectorAll(".card__media-slide");
    const navButtons = card.querySelectorAll(".card__media-nav-button");
    const carousel = card.querySelector(".card__media-carousel");
    let currentIndex = 0;
    let isCarouselActive = false;
    let hoverTimeout;

    // Check if desktop (990px and above)
    const isDesktop = () => window.innerWidth >= 990;

    // Function to show specific slide
    function showSlide(index) {
      mediaSlides.forEach((slide, i) => {
        slide.classList.toggle("active", i === index);
      });
      currentIndex = index;
    }

    // Function to handle video playback
    function handleVideoPlayback(slideIndex) {
      mediaSlides.forEach((slide, i) => {
        const video = slide.querySelector(".card__video-autoplay");
        const iframe = slide.querySelector("iframe.card__video-autoplay");

        if (video) {
          if (i === slideIndex) {
            video.currentTime = 0;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }

        // Handle external videos (YouTube/Vimeo iframes)
        if (iframe) {
          if (i === slideIndex) {
            // For external videos, we can't control playback directly
            // They should autoplay when visible due to autoplay parameter
          } else {
            // Pause external videos by reloading without autoplay
            if (iframe.src && iframe.src.includes("autoplay=1")) {
              const pausedSrc = iframe.src.replace("autoplay=1", "autoplay=0");
              iframe.src = pausedSrc;
            }
          }
        }
      });
    }

    // Pause video when not in view (for performance)
    const hasVideo = card.querySelector(".card__video-autoplay");
    if (hasVideo) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (currentIndex === 0) {
                const activeVideo = mediaSlides[0].querySelector(
                  ".card__video-autoplay",
                );
                if (activeVideo && activeVideo.tagName === "VIDEO") {
                  activeVideo.play().catch(() => {});
                }
              }
            } else {
              mediaSlides.forEach((slide) => {
                const video = slide.querySelector("video.card__video-autoplay");
                const iframe = slide.querySelector(
                  "iframe.card__video-autoplay",
                );
                if (video) video.pause();
                // For iframes, we can't pause directly, but they'll stop when out of view
              });
            }
          });
        },
        { threshold: 0.5 },
      );
      observer.observe(card);
    }

    // Handle media navigation (only on mobile/tablet)
    navButtons.forEach((button) => {
      button.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();

        // Only allow navigation on mobile/tablet
        if (isDesktop()) {
          return;
        }

        const direction = this.dataset.direction;
        let newIndex;

        // Mark carousel as actively being used
        isCarouselActive = true;
        carousel.classList.add("carousel-active");

        // Calculate new index
        if (direction === "next") {
          newIndex =
            currentIndex + 1 >= mediaSlides.length ? 0 : currentIndex + 1;
        } else {
          newIndex =
            currentIndex - 1 < 0 ? mediaSlides.length - 1 : currentIndex - 1;
        }

        // Show the new slide
        showSlide(newIndex);
        handleVideoPlayback(newIndex);

        // Clear any hover timeout
        clearTimeout(hoverTimeout);

        // Remove carousel-active class after a delay
        setTimeout(() => {
          isCarouselActive = false;
          carousel.classList.remove("carousel-active");
        }, 1500);
      });
    });

    // Handle hover behavior
    card.addEventListener("mouseenter", function () {
      // Don't trigger hover if carousel is being used or there's only one slide
      if (isCarouselActive || mediaSlides.length < 2) {
        return;
      }

      if (isDesktop()) {
        // Desktop: Only show second image on hover
        if (currentIndex === 0) {
          hoverTimeout = setTimeout(() => {
            showSlide(1);
            handleVideoPlayback(1);
          }, 50); // Small delay to prevent accidental triggers
        }
      } else {
        // Mobile/Tablet: No hover behavior (carousel only)
        return;
      }
    });

    // Handle mouse leave to return to first image (desktop only)
    card.addEventListener("mouseleave", function () {
      clearTimeout(hoverTimeout);

      // Don't trigger if carousel is being used or not on desktop
      if (isCarouselActive || !isDesktop()) {
        return;
      }

      // Only return to first slide if we were showing the hover slide (index 1)
      if (currentIndex === 1 && !isCarouselActive) {
        showSlide(0);
        handleVideoPlayback(0);
      }
    });

    // Handle navigation area hover to prevent conflicts (mobile/tablet only)
    const navArea = card.querySelector(".card__media-nav");
    if (navArea) {
      navArea.addEventListener("mouseenter", function () {
        if (!isDesktop()) {
          clearTimeout(hoverTimeout);
          isCarouselActive = true;
        }
      });

      navArea.addEventListener("mouseleave", function () {
        if (!isDesktop()) {
          setTimeout(() => {
            isCarouselActive = false;
          }, 300);
        }
      });
    }
  });
}

// Initialize on DOM load
document.addEventListener("DOMContentLoaded", function () {
  initializeProductCardHandlers();
});

// Handle swatch expand/collapse functionality using event delegation
function handleSwatchExpandCollapse(event) {
  // Check if clicked element is an expand button
  if (event.target.closest(".swatch-expand-btn")) {
    const button = event.target.closest(".swatch-expand-btn");
    event.preventDefault();
    event.stopPropagation();

    const productId = button.dataset.productId;
    const swatchContainer = button.closest(".product-form__input--swatch");
    const hiddenSwatches = swatchContainer.querySelectorAll(".swatch-hidden");
    const collapseBtn = swatchContainer.querySelector(".swatch-collapse-btn");

    // Get current market prefix from URL
    const currentPath = window.location.pathname;
    let marketPrefix = "";
    const marketMatch = currentPath.match(/^(\/[a-z]{2}-[a-z]{2})/i);
    if (marketMatch) {
      marketPrefix = marketMatch[1];
    }

    // Filter swatches based on market availability
    const availableSwatches = Array.from(hiddenSwatches).filter((swatch) => {
      const swatchLink = swatch.querySelector(".card-swatch, .swatch-link");
      if (!swatchLink) return false;

      const swatchUrl = swatchLink.getAttribute("data-url");
      if (!swatchUrl) return false;

      // Check if swatch URL matches current market prefix
      // If we're on a market-specific URL, swatches should match that market
      if (marketPrefix) {
        const swatchUrlPath = swatchUrl.split("?")[0].split("#")[0]; // Remove query params and hash

        // If swatch URL has a different market prefix (e.g., /en-us/products/... when we're on /en-nz)
        // Check if it starts with a different market prefix pattern
        const otherMarketMatch = swatchUrlPath.match(/^(\/[a-z]{2}-[a-z]{2})/i);
        if (otherMarketMatch && otherMarketMatch[1] !== marketPrefix) {
          // Different market - hide this swatch
          return false;
        }

        // If swatch URL starts with the market prefix, it's available
        if (swatchUrlPath.startsWith(marketPrefix)) {
          return true;
        }

        // If swatch URL is relative (/products/...), show it - we'll normalize it to current market
        // This handles cases where Shopify doesn't include market prefix in product.url
        if (swatchUrlPath.startsWith("/products/")) {
          return true;
        }

        // If we can't determine and it doesn't have a market prefix, show it
        // (it will be normalized to current market)
        return true;
      } else {
        // No market prefix in current URL - show all swatches
        return true;
      }
    });

    // Normalize swatch URLs to current market prefix
    const normalizeSwatchUrl = (url, currentMarketPrefix) => {
      if (!url) return url;

      // Remove any existing market prefix
      let normalizedUrl = url.replace(/^\/[a-z]{2}-[a-z]{2}/i, "");

      // Ensure it starts with /
      if (!normalizedUrl.startsWith("/")) {
        normalizedUrl = "/" + normalizedUrl;
      }

      // Add current market prefix if we have one
      if (currentMarketPrefix) {
        normalizedUrl = currentMarketPrefix + normalizedUrl;
      }

      return normalizedUrl;
    };

    // Show available swatches with animation
    availableSwatches.forEach((swatch, index) => {
      setTimeout(() => {
        // Remove any inline display styles first, then set to flex
        swatch.removeAttribute("style");
        swatch.style.display = "flex";
        swatch.style.opacity = "0";
        swatch.style.transform = "scale(0.8)";
        swatch.style.transition = "opacity 0.3s ease, transform 0.3s ease";

        // Normalize swatch URL to current market prefix
        const swatchLink = swatch.querySelector(".card-swatch, .swatch-link");
        if (swatchLink) {
          const swatchUrl = swatchLink.getAttribute("data-url");
          if (swatchUrl) {
            // Normalize URL to ensure it has the correct market prefix
            const normalizedUrl = normalizeSwatchUrl(swatchUrl, marketPrefix);
            swatchLink.setAttribute("data-url", normalizedUrl);
          }
        }

        // Trigger animation
        requestAnimationFrame(() => {
          swatch.style.opacity = "1";
          swatch.style.transform = "scale(1)";
        });
      }, index * 50); // Stagger the animation
    });

    // Hide swatches that aren't available in current market
    Array.from(hiddenSwatches).forEach((swatch) => {
      if (!availableSwatches.includes(swatch)) {
        swatch.style.display = "none";
      }
    });

    // Hide expand button and show collapse button
    button.style.display = "none";
    if (collapseBtn) {
      collapseBtn.style.display = "flex";
    }
    return;
  }

  // Check if clicked element is a collapse button
  if (event.target.closest(".swatch-collapse-btn")) {
    const button = event.target.closest(".swatch-collapse-btn");
    event.preventDefault();
    event.stopPropagation();

    const productId = button.dataset.productId;
    const swatchContainer = button.closest(".product-form__input--swatch");
    const hiddenSwatches = swatchContainer.querySelectorAll(".swatch-hidden");
    const expandBtn = swatchContainer.querySelector(".swatch-expand-btn");

    // Hide swatches with animation
    hiddenSwatches.forEach((swatch, index) => {
      setTimeout(() => {
        swatch.style.opacity = "0";
        swatch.style.transform = "scale(0.8)";

        setTimeout(() => {
          swatch.style.display = "none";
        }, 300);
      }, index * 30); // Stagger the animation
    });

    // Hide collapse button and show expand button
    button.style.display = "none";
    if (expandBtn) {
      setTimeout(() => {
        expandBtn.style.display = "flex";
      }, 300);
    }
    return;
  }
}

// Handle keyboard accessibility for expand/collapse buttons
function handleSwatchExpandCollapseKeyboard(event) {
  if (event.key === "Enter" || event.key === " ") {
    if (
      event.target.closest(".swatch-expand-btn") ||
      event.target.closest(".swatch-collapse-btn")
    ) {
      event.preventDefault();
      event.target.click();
    }
  }
}

// Initialize swatch expand/collapse functionality - using a flag to prevent multiple initializations
let swatchExpandCollapseInitialized = false;

function initializeSwatchExpandCollapse() {
  if (swatchExpandCollapseInitialized) {
    return;
  }

  // Add event delegation listeners
  document.addEventListener("click", handleSwatchExpandCollapse);
  document.addEventListener("keydown", handleSwatchExpandCollapseKeyboard);

  swatchExpandCollapseInitialized = true;
}

// Make the function globally available
window.initializeProductCardHandlers = initializeProductCardHandlers;
window.initializeSwatchExpandCollapse = initializeSwatchExpandCollapse;

// Initialize on DOM load
document.addEventListener("DOMContentLoaded", function () {
  initializeSwatchExpandCollapse();
});

// Reinitialize when new products are added (but the flag prevents duplicate listeners)
document.addEventListener("products:added", function () {
  initializeProductCardHandlers();
  initializeSwatchExpandCollapse();
});
